// The inspector: the whole of the selection, as one card (PLAN-UI §28.3, U6;
// screen-mock `#insp`, relocation-map's Inspector zone).
//
// **One card, everything inline** (designer ruling, U6 second review,
// 2026-09-02). U6 shipped this card, then a board-anchored action bar, then a
// `···` door onto a second panel of dense policy. All three are gone in the
// direction of the same ruling: the selection has one home. The bar covered
// the squares it was about and re-projected on every frame the camera moved;
// the door was a click and a placement problem in front of a store's taps, and
// the player is fine with actions and dense policy taking space in the card.
// So `ContextPopover.tsx` died the way `ActionBar.tsx` did, and its sections
// are drawn here under the controls row. `action-bar` stays as that row's test
// id and every section id is the shelf's own.
//
// **The card may grow, and it is capped rather than trusted.** A foundry with
// its forge list is the tall case; `MAX_HEIGHT` keeps it inside the viewport and it scrolls past that, because a card whose bottom
// is off screen is a control nobody can reach.
//
// **The controls carry their own state, so the rows that restated it are
// gone** (first review): no `Policy` row, no `Saving for` row, no king's tempo
// line as prose. A lit Dump toggle says what a `Policy` row repeated, and the
// lit rank tile in the Saving-for section is the saving readout — its card
// carries the coalition-priced bill and whether it is ready or short. Fewer
// words is the standing instruction.
//
// **Every control explains itself on a `HoverCard`, never a native `title`**
// (same review): the name as the card's heading, the reason or rule as its
// body, a bill as swatched rows where there is one. The engine writes all of
// it (`ThingCard`, PLAN-CLIENT-SPLIT day 8); nothing here decides what a
// control says, and a click is `session.choose(option)`. **Where** the card goes is
// this file's: **every card opens `right-start` off the card's own edge, out
// over the board**, which takes no pointer. The rows are stacked, so `bottom`
// from a control in one row is the top of the next — the Dump card landed on
// the rank picker, which is the thing the second review was about. And `right`
// of the *trigger* is not out either: a control at this card's left edge is
// 200px of card away from open air, so the Dump button's card came down on the
// Saving-for heading instead. So every card here passes `beside` — this card's
// own element — and hangs off its right edge at the trigger's own height
// (`kit/hoverAnchor.ts`). One ref, two readers: the inset registry measures
// this element and every hover card anchors beside it.
//
// **Registered with `edge: null`**, which is the load-bearing line here. The
// inset registry feeds the camera what the interface covers, and a card that
// appears and vanishes with a selection would pan the board every time you
// clicked a piece. It is measured — hints and overlays still clip against it —
// and it is not an edge the camera backs away from.
//
// Explicit props, no store reads: the card is memoised once in the engine's
// chrome, and a component that fetched its own state would re-derive the whole
// model on every render of everything above it. `Hotbar`'s arrangement.
//
// The card's own layout is here; the parts it is made of are in `./inspector/`
// — `Section` and its `Tap`, the row's `Control`, the `Explained` hover card
// they share, the `Under` line, `useCardBox` (the registration and the `beside`
// element, shared with `GroundCard`), and the two files of `styled` primitives.

import type { OptionId, ThingCard } from "src/api";
import { UnitChips } from "./unitRows";
import { HoverCard } from "./kit";
import { Control } from "./inspector/Control";
import { Section } from "./inspector/Section";
import { Under } from "./inspector/Under";
import { useCardBox } from "./inspector/useCardBox";
import {
  Block,
  Card,
  Controls,
  Fill,
  Name,
  Note,
  Pip,
  PipGlyph,
  Pips,
  Row,
  Rule,
  Track,
  Value,
  Where
} from "./inspector/styles";

export interface InspectorProps {
  /** The thing standing on the selected square, as the engine describes it. */
  card: ThingCard;
  /** `session.choose`. */
  choose(option: OptionId): void;
}

/** How the owner reads under the name. Three words, and they are the contract's. */
export const OWNER: Record<ThingCard["owner"], string> = {
  yours: "yours",
  ally: "ally",
  enemy: "enemy"
};

export function Inspector({ card: model, choose }: InspectorProps) {
  const { root, box } = useCardBox();
  const { controls, sections, under } = model;

  return (
    <Card ref={root} data-testid="inspector">
      {/* The rule the thing obeys is a hover off its name, not a paragraph in
          the card — "the rule a control obeys goes in a tooltip; the label
          stays", and fewer words is the standing instruction. Marked with the
          dotted underline and the help cursor, or it is a deletion. */}
      <Name>
        <HoverCard
          card={model.detail}
          title={model.name}
          placement="right-start"
          beside={box}
        >
          <Rule>{model.name}</Rule>
        </HoverCard>
        <Where>
          {model.square} · {OWNER[model.owner]}
        </Where>
      </Name>

      {model.pips.length > 0 && (
        <Pips>
          {model.pips.map((pip, index) => (
            <Pip key={index}>
              <PipGlyph className={`pip-${pip.tone}`}>{pip.glyph}</PipGlyph>
              {pip.label}
            </Pip>
          ))}
        </Pips>
      )}

      {model.stats && (
        <>
          <Row data-testid="piece-stats">
            <span>Blow / Health</span>
            <Value>
              {model.stats.blow} · {model.stats.health}
            </Value>
          </Row>
          {/* Under the health it stands above, and only while worn, like
              Holds: a row of noughts is a row nobody reads. */}
          {model.stats.armour > 0 && (
            <Row data-testid="piece-armour">
              <span>Armour</span>
              <Value>{model.stats.armour}</Value>
            </Row>
          )}
          <Row>
            <span>Leaves</span>
            <Value>{model.stats.leaves}</Value>
          </Row>
        </>
      )}

      {model.holdings.length > 0 && (
        <Row>
          <span>Holds</span>
          <UnitChips holdings={model.holdings} beside={box} />
        </Row>
      )}

      {/* The ground it is standing on, which is the piece's own business: a
          pawn on a seam is mining it. */}
      {under && <Under row={under} />}

      {model.progress && (
        <Track>
          <Fill
            className={model.progress.ready ? "ready" : undefined}
            style={{ width: `${Math.round(model.progress.fraction * 100)}%` }}
          />
        </Track>
      )}

      {model.army && (
        <>
          <Row>
            <span>Army</span>
            <Value>{model.army.army}</Value>
          </Row>
          {model.army.profile !== null && (
            <Row>
              <span>Profile</span>
              <Value>{model.army.profile}</Value>
            </Row>
          )}
        </>
      )}

      {/* Last of the readouts, and the rule behind it is a hover — both
          sentences were `Legend` hints in the shelf and neither has anywhere
          else to live. */}
      {model.note !== null && (
        <HoverCard
          card={model.note.hint}
          title={model.note.text}
          placement="right-start"
          beside={box}
        >
          <Note>{model.note.text}</Note>
        </HoverCard>
      )}

      {/* **The controls, in the card** (designer ruling, U6 review). It wraps
          — a king with three free actions and a dump toggle is four —
          and `action-bar` is the row's id because that is what every smoke and
          this phase's own notes call it. */}
      {controls.length > 0 && (
        <Controls data-testid="action-bar">
          {controls.map((item) => (
            <Control key={item.id} item={item} choose={choose} beside={box} />
          ))}
        </Controls>
      )}

      {/* **And the dense half under it, inline** (second review). Each block
          takes the same divider the readout rows do, so the card reads as one
          column of things rather than as a card with a panel stapled on. */}
      {sections.map((section) => (
        <Block key={section.kind}>
          <Section section={section} choose={choose} beside={box} />
        </Block>
      ))}
    </Card>
  );
}
